import React, { useState } from 'react';
import { MdArrowBack, MdTrendingUp } from 'react-icons/md';
import { CATEGORY_COLORS, MOCK_MONTHLY_INCOME } from '../constants/colors';
import { categorizeTransaction } from '../utils/categorizeTransaction';
import txData from '../data/transactions.json';

const transactionsData = txData.transactionsData;

const SpendingAnalysisScreen = ({ onNavigate, onBack }) => {
  const [frequency, setFrequency] = useState('Monthly');

  const getLabels = () => {
    if (frequency === 'Weekly') {
      return ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
    } else if (frequency === 'Monthly') {
      return ['1', '5', '10', '15', '20', '25', '31'];
    } else {
      return ['J', 'F', 'M', 'A', 'M', 'J', 'J', 'A', 'S', 'O', 'N', 'D'];
    }
  };

  const getChartData = () => {
    const labels = getLabels();
    const categories = ['Groceries', 'Home', 'Entertainment', 'Travel'];
    const chartData = labels.map(() => ({
      Groceries: 0, Home: 0, Entertainment: 0, Travel: 0
    }));

    transactionsData.forEach(group => {
      group.items.forEach(item => {
        if (!item.isPositive && item.datetime) {
          const date = new Date(item.datetime);
          const amount = parseFloat(item.amount.replace('-$', '').replace(',', ''));
          const category = categorizeTransaction(item.merchant);

          let periodIndex = 0;
          if (frequency === 'Weekly') {
            periodIndex = date.getDay() === 0 ? 6 : date.getDay() - 1;
          } else if (frequency === 'Monthly') {
            const dayOfMonth = date.getDate();
            if (dayOfMonth <= 1) periodIndex = 0;
            else if (dayOfMonth <= 5) periodIndex = 1;
            else if (dayOfMonth <= 10) periodIndex = 2;
            else if (dayOfMonth <= 15) periodIndex = 3;
            else if (dayOfMonth <= 20) periodIndex = 4;
            else if (dayOfMonth <= 25) periodIndex = 5;
            else periodIndex = 6;
          } else {
            periodIndex = date.getMonth();
          }

          if (chartData[periodIndex] && chartData[periodIndex][category] !== undefined) {
            chartData[periodIndex][category] += amount;
          }
        }
      });
    });

    return chartData;
  };

  const labels = getLabels();
  const chartData = getChartData();
  const categories = ['Groceries', 'Home', 'Entertainment', 'Travel'];

  const totals = { Groceries: 0, Home: 0, Entertainment: 0, Travel: 0 };
  const maxValue = Math.max(...chartData.map(d => Object.values(d).reduce((a, b) => a + b, 0)));

  chartData.forEach(period => {
    Object.keys(totals).forEach(cat => {
      totals[cat] += period[cat];
    });
  });

  const totalSpent = Object.values(totals).reduce((a, b) => a + b, 0);

  const spendingRatio = totalSpent / (MOCK_MONTHLY_INCOME * (frequency === 'Yearly' ? 12 : (frequency === 'Weekly' ? 0.25 : 1)));
  let healthStatus = { label: 'Balanced', color: '#008a0d', tip: 'Great job! You are well within your budget.' };

  if (spendingRatio > 0.9) {
    healthStatus = { label: 'High', color: 'var(--td-red)', tip: 'Consider reducing spend in top categories to save more.' };
  } else if (spendingRatio < 0.5) {
    healthStatus = { label: 'Excellent', color: '#008a0d', tip: 'You are saving a significant portion of your income!' };
  }

  return (
    <div className="screen spending-analysis-screen bh-flow">
      <header className="bh-flow-hero">
        <div className="bh-flow-hero-left">
          <button className="bh-flow-back" onClick={onBack || (() => onNavigate('credit'))} aria-label="Back to credit">
            <MdArrowBack />
          </button>
          <h1 className="bh-flow-title">Spending Analysis</h1>
        </div>
      </header>

      <div className="scroll-content gray-bg">
        <div className="spending-summary-card">
          <h1 className="total-spend-amount">${totalSpent.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</h1>
          <p className="spend-subtitle">Total {frequency.toLowerCase()} spending</p>
        </div>

        <div className="white-container chart-card">
          <div className="chart-header">
            <h3 className="chart-title">Spending Trends</h3>
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'flex-end', height: '300px', gap: '8px', padding: '20px 10px', borderBottom: '1px solid #ddd' }}>
            {chartData.map((d, idx) => {
              const total = Object.values(d).reduce((a, b) => a + b, 0);
              const barHeight = maxValue > 0 ? (total / maxValue) * 100 : 0;

              return (
                <div key={idx} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-end', gap: '4px', height: '100%' }}>
                  {total > 0 && (
                    <span style={{ fontSize: '11px', fontWeight: '600', color: 'var(--text-dark)', marginBottom: '4px' }}>
                      ${total.toFixed(0)}
                    </span>
                  )}
                  <div style={{ width: '100%', height: `${barHeight}%`, display: 'flex', flexDirection: 'column', backgroundColor: 'transparent', minHeight: '20px' }}>
                    {categories.map(cat => {
                      const catHeight = total > 0 ? (d[cat] / total) * 100 : 0;
                      return (
                        <div
                          key={cat}
                          style={{
                            height: `${catHeight}%`,
                            backgroundColor: CATEGORY_COLORS[cat],
                            borderRadius: catHeight > 5 ? '2px' : '0px',
                            minHeight: d[cat] > 0 ? '4px' : '0px'
                          }}
                        />
                      );
                    })}
                  </div>
                  <span style={{ fontSize: '12px', fontWeight: '500', color: 'var(--text-medium)', textAlign: 'center', marginTop: '4px' }}>{labels[idx]}</span>
                </div>
              );
            })}
          </div>

          <div className="chart-legend" style={{ display: 'flex', gap: '16px', padding: '16px', justifyContent: 'center', flexWrap: 'wrap', backgroundColor: 'var(--bg-light-gray)', borderRadius: '0 0 8px 8px' }}>
            {categories.map(cat => (
              <div key={cat} className="legend-item" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <span className="legend-dot" style={{ backgroundColor: CATEGORY_COLORS[cat], width: '12px', height: '12px', borderRadius: '2px' }}></span>
                <span style={{ fontSize: '13px', color: 'var(--text-dark)' }}>{cat}</span>
              </div>
            ))}
          </div>

          <div style={{ display: 'flex', gap: '8px', justifyContent: 'center', padding: '16px' }}>
            {['Weekly', 'Monthly', 'Yearly'].map(freq => (
              <button
                key={freq}
                className={`freq-toggle-btn ${frequency === freq ? 'active' : ''}`}
                onClick={() => setFrequency(freq)}
                style={{
                  padding: '8px 16px',
                  borderRadius: '4px',
                  border: frequency === freq ? '2px solid #008a0d' : '1px solid #ddd',
                  backgroundColor: frequency === freq ? '#008a0d' : 'white',
                  color: frequency === freq ? 'white' : '#333',
                  cursor: 'pointer',
                  fontWeight: '600',
                  fontSize: '14px'
                }}
              >
                {freq}
              </button>
            ))}
          </div>
        </div>

        <div className="white-container insights-container">
          <div className="insight-header">
            <MdTrendingUp style={{ color: healthStatus.color }} size={24} />
            <span className="insight-title">Financial Health: <span style={{ color: healthStatus.color }}>{healthStatus.label}</span></span>
          </div>
          <p className="insight-body">
            You've spent <strong>${totalSpent.toFixed(2)}</strong> this {frequency.toLowerCase()}.
          </p>
          <div className="insight-tip-box">
            <strong>Tip:</strong> {healthStatus.tip}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SpendingAnalysisScreen;
